import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    CircularProgress,
    Alert,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableRow,
    TableHead,
    Chip
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { AppButton } from '../../../shared/components/AppButton';
import { getCmsPageBySlug } from '../../../shared/services/cmsService';
import { CmsPageAttributes } from '../../../shared/types/cms';
import moment from 'moment';

export const ViewCmsPage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const [page, setPage] = useState<CmsPageAttributes | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!slug) {
            setError('Invalid page slug');
            setLoading(false);
            return;
        }
        const fetchPage = async () => {
            try {
                setLoading(true);
                setError('');
                const response = await getCmsPageBySlug(slug);
                if (response.success && response.data?.page) {
                    setPage(response.data.page);
                } else {
                    setError('Page not found');
                }
            } catch {
                setError('Failed to load page');
                setPage(null);
            } finally {
                setLoading(false);
            }
        };
        fetchPage();
    }, [slug]);

    if (loading) {
        return (
            <div className="flex flex-col flex-auto min-w-0">
                <div className="flex flex-auto p-6 sm:p-10 items-center justify-center min-h-[300px]">
                    <CircularProgress />
                </div>
            </div>
        );
    }

    if (error || !page) {
        return (
            <div className="flex flex-col flex-auto min-w-0">
                <div className="p-6 sm:p-10">
                    <AppButton variant="secondary" startIcon={<ArrowBackIcon />} onClick={() => navigate('/cms')}>
                        Back to CMS Pages
                    </AppButton>
                    <Box mt={2}>
                        <Alert severity="error">{error || 'Page not found'}</Alert>
                    </Box>
                </div>
            </div>
        );
    }

    const infoRows: { label: string; value: React.ReactNode }[] = [
        { label: 'Title', value: page.title },
        { label: 'Slug', value: page.slug },
        { label: 'Meta Title', value: page.metaTitle || '-' },
        { label: 'Meta Description', value: page.metaDescription || '-' },
        {
            label: 'Status',
            value: (
                <Chip
                    label={page.isActive ? 'ACTIVE' : 'INACTIVE'}
                    size="small"
                    color={page.isActive ? 'success' : 'default'}
                    style={{ borderRadius: '6px' }}
                />
            )
        },
        { label: 'Created At', value: moment(page.createdAt).format('DD MMM YYYY, hh:mm A') },
        { label: 'Updated At', value: moment(page.updatedAt).format('DD MMM YYYY, hh:mm A') },
    ];

    return (
        <div className="flex flex-col flex-auto min-w-0">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-6 sm:py-8 sm:px-10 border-b border-gray-200 bg-white">
                <div className="flex items-center gap-4">
                    <AppButton variant="secondary" startIcon={<ArrowBackIcon />} onClick={() => navigate('/cms')}>
                        Back
                    </AppButton>
                    <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900">
                        {page.title}
                    </h2>
                </div>
            </div>

            {/* Content */}
            <div className="flex flex-auto p-6 sm:p-10">
                <div className="flex flex-col w-full gap-6">
                    {/* Info Table */}
                    <Paper elevation={1} className="overflow-hidden">
                        <Table>
                            <TableHead>
                                <TableRow className="bg-gray-100">
                                    <TableCell className="!font-semibold !text-gray-700" width="200">Field</TableCell>
                                    <TableCell className="!font-semibold !text-gray-700">Value</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {infoRows.map((row, i) => (
                                    <TableRow key={row.label} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                        <TableCell className="!font-medium !text-gray-700">{row.label}</TableCell>
                                        <TableCell className="!text-gray-900">{row.value}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>

                    {/* HTML Content Preview */}
                    <Paper elevation={1} className="p-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-4">Content Preview</h3>
                        <div
                            className="prose max-w-none border border-gray-200 rounded-lg p-4 bg-gray-50"
                            dangerouslySetInnerHTML={{ __html: page.content }}
                        />
                    </Paper>
                </div>
            </div>
        </div>
    );
};
